import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { getApprovedCenters } from "../../../store/actions/centerActions/studentAction";
import { assignToCenters } from "../../../store/actions/councilActions/queryAssignAction";

/*
  Give one query to one or more centers.

  A dropdown that only takes a single center would mean opening this once per
  center, so the list is a search box over every approved center with a tick
  against each. What is ticked when Save is pressed becomes the whole list -
  unticking a center takes the query away from it.

  The center only ever reads what it is given; nothing here lets a center
  write back.
*/
const AssignCenters = ({ show, onHide, source, queryId, label, current, onSaved }) => {
  const dispatch = useDispatch();

  const [centers, setCenters] = useState([]);
  const [picked, setPicked] = useState([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!show) return;
    dispatch(getApprovedCenters()).then((list) =>
      setCenters(Array.isArray(list) ? list : [])
    );
    setPicked((current || []).map((c) => String(c.center_id)));
    setSearch("");
  }, [show, queryId]);

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return centers;
    return centers.filter((c) =>
      [c.center_name, c.center_code, c.center_city, c.center_state]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [centers, search]);

  const toggle = (id) => {
    const key = String(id);
    setPicked((list) =>
      list.includes(key) ? list.filter((x) => x !== key) : [...list, key]
    );
  };

  const save = async () => {
    setBusy(true);
    // replace: what is ticked is the full list, so unticking removes.
    const result = await dispatch(
      assignToCenters(source, queryId, picked, true)
    );
    setBusy(false);

    if (result !== false) {
      onSaved && onSaved(queryId, result);
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Assign to Centers</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {label && <p class="mb-2 text-muted">{label}</p>}

        <input
          type="text"
          class="form-control mb-2"
          placeholder="Search center by name, code, city or state"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div class="mb-2">
          <b>{picked.length}</b> center(s) selected
          {picked.length > 0 && (
            <button
              type="button"
              class="btn btn-sm btn-link"
              onClick={() => setPicked([])}
            >
              clear all
            </button>
          )}
        </div>

        <div
          style={{ maxHeight: "320px", overflowY: "auto" }}
          class="border rounded p-2"
        >
          {shown.length === 0 ? (
            <div class="text-muted p-2">No center matches that search.</div>
          ) : (
            shown.map((c) => (
              <label
                key={c.id}
                class="d-block mb-1"
                style={{ cursor: "pointer" }}
              >
                <input
                  type="checkbox"
                  class="mr-2"
                  checked={picked.includes(String(c.id))}
                  onChange={() => toggle(c.id)}
                />
                {c.center_name}
                {c.center_code ? ` (${c.center_code})` : ""}
                {c.center_city ? ` - ${c.center_city}` : ""}
              </label>
            ))
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" class="btn btn-secondary" onClick={onHide}>
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-comman"
          disabled={busy}
          onClick={save}
        >
          {busy ? "Saving..." : "Save"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default AssignCenters;
